import React, { forwardRef, useEffect, useRef } from 'react';

export const TextArea = forwardRef(
    (
        {
            value,
            placeholder,
            rows,
            cols,
            onChange,
            onBlur,
            className = '',
            resize,
            disabled,
            autoScroll = true,
            moveCaretToEndOnFocus = true, //  renamed for clarity
            hoverText,
            // backwards compatibility: if some callers still pass disabledText,
            // we treat it as hover text when present.
            disabledText,
        },
        ref
    ) => {
        const internalRef = useRef(null);
        const prevValueRef = useRef(value);
        const userInteractingRef = useRef(false); // track if user is actively typing

        // Combine forwarded ref and internal ref
        const setRefs = (node) => {
            internalRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
        };

        useEffect(() => {
            const textarea = internalRef.current;
            if (!textarea) return;

            // only auto-scroll if content exceeds height (not on every keystroke)
            if (
                autoScroll &&
                textarea.scrollHeight > textarea.clientHeight &&
                !userInteractingRef.current
            ) {
                const scrollBottom =
                    textarea.scrollHeight - textarea.clientHeight;
                if (Math.abs(textarea.scrollTop - scrollBottom) < 5) {
                    textarea.scrollTop = textarea.scrollHeight;
                }
            }

            // only respond to external updates (not user typing)
            if (!userInteractingRef.current && value !== prevValueRef.current) {
                // external update detected, do not move caret unless refocused
            }

            prevValueRef.current = value;
        }, [value, autoScroll]);

        return (
            <textarea
                ref={setRefs}
                className={`${cols ? '' : 'w-full'} ${
                    rows ? '' : 'h-52'
                } p-2.5 rounded-xl
                hover:outline-none focus:outline-backgrounds-primary200 border
                hover:border-backgrounds-primary300
                ${
                    disabled
                        ? 'bg-backgrounds-secondary'
                        : 'bg-backgrounds-white'
                }
                ${className}`}
                placeholder={placeholder}
                rows={rows}
                cols={cols}
                value={value}
                onChange={(e) => {
                    userInteractingRef.current = true; // mark user interaction
                    onChange?.(e);
                }}
                onBlur={(e) => {
                    userInteractingRef.current = false; // reset after editing ends
                    onBlur?.(e);
                }}
                onFocus={(e) => {
                    userInteractingRef.current = false; // fresh focus
                    if (moveCaretToEndOnFocus) {
                        const len = e.target.value.length;
                        e.target.setSelectionRange(len, len);
                    }
                }}
                style={{ resize: resize || 'both' }}
                disabled={disabled}
                title={
                    hoverText
                        ? hoverText
                        : disabledText
                        ? disabledText
                        : undefined
                }
            />
        );
    }
);
